/**
 * Briefs-tab TanStack Query hooks (Phase 4 Plan 08 Task 1).
 *
 * One hook per endpoint, each unwrapping the server envelope and returning
 * the inner payload. Query keys start with `['briefs', <endpoint>,
 * …, filtersQuestion(filters)]` so the cache is keyed by the semantic filter
 * set (not the raw URL).
 *
 * `staleTime: 60_000` + `placeholderData: keepPreviousData` — and since the
 * recount rule, the second of those no longer means the previous filter set's
 * answer stays on screen. `useFilteredQuery` reports that window as "nothing
 * counted yet" (hooks/use-filtered-query.ts), so the tab blanks and says so.
 * What `keepPreviousData` still buys: telling a recount apart from a first
 * load, and an instant switch back to a filter set already in the cache.
 *
 * Trend-granularity participates in the query key so changing granularity
 * triggers a refetch (not a cache hit on the wrong bucket size).
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import { filtersQuestion, type Filters } from '@/lib/filters-schema';
import {
  fetchBriefComplexityDist,
  fetchBriefFailureReasons,
  fetchBriefLog,
  fetchBriefMessages,
  fetchBriefMessagesByTrace,
  fetchBriefTrend,
  fetchBriefsKpis,
} from '@/services/briefs-api';
import type {
  BriefComplexityCell,
  BriefFailureReason,
  BriefLogRow,
  BriefMessages,
  BriefTrendPoint,
  BriefsKpis,
} from '@/services/api-types';

const STALE_TIME = 60_000;

export function useBriefsKpis(filters: Filters) {
  return useFilteredQuery<BriefsKpis>({
    queryKey: ['briefs', 'kpis', filtersQuestion(filters)],
    queryFn: async () => (await fetchBriefsKpis(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useBriefTrend(
  filters: Filters,
  granularity: Filters['granularity'],
) {
  return useFilteredQuery<BriefTrendPoint[]>({
    queryKey: [
      'v2',
      'briefs',
      'trend',
      granularity,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await fetchBriefTrend(filters, granularity)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useBriefLog(filters: Filters) {
  return useFilteredQuery<BriefLogRow[]>({
    queryKey: ['briefs', 'log', filtersQuestion(filters)],
    queryFn: async () => (await fetchBriefLog(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useBriefFailureReasons(filters: Filters) {
  return useFilteredQuery<BriefFailureReason[]>({
    queryKey: ['briefs', 'failure-reasons', filtersQuestion(filters)],
    queryFn: async () => (await fetchBriefFailureReasons(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useBriefComplexityDist(filters: Filters) {
  return useFilteredQuery<BriefComplexityCell[]>({
    queryKey: ['briefs', 'complexity-dist', filtersQuestion(filters)],
    queryFn: async () => (await fetchBriefComplexityDist(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Inline brief-message viewer (Phase 8)
// ---------------------------------------------------------------------------

/**
 * Lazy fetch for one RM/date brief content — only runs when `enabled` is true,
 * which `BriefMessageViewer` toggles to the row's expanded state. React Query
 * caches the result in-memory keyed by `[userId, date]`, so a collapse +
 * re-expand re-uses the cached payload (requirement: no refetch on toggle).
 *
 * Persisted briefs never change after send-time, so `staleTime: Infinity` is
 * the correct semantic — the cache only expires on full unmount of the page.
 *
 * 404 surfaces as an `ApiError` with status 404 — the viewer treats that as a
 * first-class state ("not persisted"), which is why we suppress retries on
 * not-found responses (`retry: (count, err) => …`).
 */
export function useBriefMessages(
  userId: string | null,
  date: string | null,
  enabled: boolean,
) {
  return useQuery<BriefMessages>({
    queryKey: ['briefs', 'messages', userId, date],
    queryFn: () => fetchBriefMessages(userId as string, date as string),
    enabled: enabled && Boolean(userId) && Boolean(date),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 30 * 60 * 1000,
    // 404 is a domain state, not a transient infra failure — don't retry it.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404) return false;
      return failureCount < 1;
    },
  });
}

/**
 * Same as `useBriefMessages` but keyed on a brief's trace id — for
 * trace-centric surfaces (Feedback / Operations / any TraceDetailPanel) that
 * know the traceId but not the (username, date) pair. 404 ("not a persisted
 * brief") is a first-class state, identical caching/retry semantics.
 */
export function useBriefMessagesByTrace(
  traceId: string | null,
  enabled: boolean,
) {
  return useQuery<BriefMessages>({
    queryKey: ['briefs', 'messages', 'by-trace', traceId],
    queryFn: () => fetchBriefMessagesByTrace(traceId as string),
    enabled: enabled && Boolean(traceId),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404) return false;
      return failureCount < 1;
    },
  });
}
